import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LayoutState } from '@common/services/layout-state';
import { LiveSearchService } from '@live/services/live-search.service';
import { RundownService } from '@live/services/rundown.service';
import { LiveActions, LiveApiActions } from '@live/store/actions/live.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { FullscreenLayoutTemplate } from '@src/assets/css/fullscreen.template.scss';
import { ModalResponseCodes, ModalService } from '@src/suite/common/components/modal/modal.service';
import { EnpsField } from '@src/suite/common/data-models/fields/enps-field';
import { PSTFieldNamesEnum } from '@src/suite/common/data-models/fields/pst-field-names';
import { SYSTEM_VS_GUID } from '@src/suite/common/enums/constants';
import { ObjectTypes } from '@src/suite/common/enums/object-types';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { FullscreenActions, MainSidecarActions, selectFullscreenRecordGuid, selectLayoutTemplate, SidecarMenu } from '@src/suite/common/store/fullscreen';
import { selectI18N } from '@src/suite/common/store/i18n/selectors/i18n.selectors';
import { ListActions } from '@src/suite/common/store/list/list.actions';
import { selectListGuids } from '@src/suite/common/store/list/list.selectors';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { RecordHttpActions, RundownRecordActions, StoriesRecordActions } from '@src/suite/common/store/records/records.actions';
import { selectRundownRecordByGuid, selectStoryRecordByGuid } from '@src/suite/common/store/records/records.selectors';
import { i18nKeys } from '@src/suite/common/utils/i18n.default';
import { from, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, mergeMap, switchMap } from 'rxjs/operators';

@Injectable()
export class LiveEffects {
    private actions$: Actions = inject(Actions);
    private liveSearchService: LiveSearchService = inject(LiveSearchService);
    private rundownService: RundownService = inject(RundownService);
    private viewStateService: LayoutState = inject(LayoutState);
    private dialog: MatDialog = inject(MatDialog);
    private store: Store = inject(Store);
    private nomWebServicesJson: NomWebServicesJson = inject(NomWebServicesJson);
    private modalService: ModalService = inject(ModalService);

    // loadRundownsByCurrentViewState$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(ApplicationActions.setCurrentRundownViewState),
    //         concatLatestFrom(() => this.store.select(selectCurrentViewState)),
    //         switchMap(([_, viewState]) => {
    //             if (viewState?.guid) {
    //                 return of(LiveApiActions.loadRundowns({ vs: viewState, poll: false }));
    //             }
    //             return of(ApplicationActions.noOpAction());
    //         })
    //     );
    // });

    // startPolling$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(LiveApiActions.startPolling),
    //         concatLatestFrom(() => this.store.select(selectCurrentViewState)),
    //         switchMap(([action, vs]) => {
    //             return interval(action.interval).pipe(
    //                 exhaustMap(() => {
    //                     return from(this.liveSearchService.getSearchResults(vs)).pipe(
    //                         map(data => LiveApiActions.loadRundownsSuccess({ rundowns: data?.data || [] })),
    //                         catchError(error => of(LiveApiActions.loadRundownsError({ error })))
    //                     );
    //                 }),
    //                 takeUntil(this.actions$.pipe(ofType(LiveApiActions.stopPolling)))
    //             );
    //         })
    //     );
    // });

    // stopPolling$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(LiveApiActions.stopPolling),
    //         map(action => {
    //             if (action.restart) {
    //                 return LiveApiActions.startPolling({ interval: 30000 });
    //             } else {
    //                 return ApplicationActions.noOpAction();
    //             }
    //         })
    //     );
    // });

    // loadCurrentRundown$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(LiveApiActions.loadCurrentRundown),
    //         switchMap(action => {
    //             // TODO: Make this better after NAB
    //             return from(this.rundownService.getRundown(action.data.Database, action.data.Path.fullPath, action.data.Guid)).pipe(
    //                 map(data => data.data),
    //                 switchMap(data => {
    //                     const actions: any[] = [LiveApiActions.loadCurrentRundownSuccess({ data })];
    //                     switch (action.panelAttach) {
    //                         case RundownPanelAttach.FullScreen:
    //                             actions.push(OpenFullscreenModalActions.openRundownFullscreenDialog({ recordGuid: data.Guid, recordType: data.Type }));
    //                             break;
    //                     }
    //                     return of(...actions);
    //                 }),
    //                 catchError(error => of(LiveApiActions.loadCurrentRundownError({ error })))
    //             );
    //         })
    //     );
    // });

    loadRundownStoredLayouts$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownStoredLayouts),
            switchMap(_ => {
                return from(this.rundownService.getStoredLayouts()).pipe(
                    map(data => LiveApiActions.loadRundownStoredLayoutsSuccess(data)),
                    catchError(error => of(LiveApiActions.loadRundownStoredLayoutsError({ error })))
                );
            })
        );
    });

    loadRundownLayout$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownLayout),
            switchMap(action => {
                return from(this.rundownService.getLayout(action.layoutType, action.name)).pipe(
                    map(data => {
                        return LiveApiActions.loadRundownLayoutSuccess(data);
                    }),
                    catchError(error => of(LiveApiActions.loadRundownLayoutError({ error })))
                );
            })
        );
    });

    error$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(LiveApiActions.loadRundownsError, LiveApiActions.loadCurrentRundownError),
                map(action => {
                    console.error(action.error);
                })
            );
        },
        { dispatch: false }
    );

    openRundownStoryFullScreen$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveActions.openRundownStoryFullScreen),
            concatLatestFrom(() => [this.store.select(selectLayoutTemplate)]),
            switchMap(([action, layout]) => {
                const actions = [];
                if (layout === FullscreenLayoutTemplate.ONLY_MAIN) {
                    actions.push(FullscreenActions.setFullscreenTemplate({ template: FullscreenLayoutTemplate.BASIC }));
                }
                actions.push(MainSidecarActions.selectSidecarStory({ guid: action.guid }));
                actions.push(MainSidecarActions.selectSidecarMenu({ menu: SidecarMenu.STORIES }));
                actions.push(MainSidecarActions.setCurrentStoryViewState({ guid: SYSTEM_VS_GUID }));
                return actions;
            })
        );
    });

    freezeRundownPages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.freezeRundownPages),
            concatLatestFrom(action => [this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.guid)), this.store.select(selectI18N)]),
            switchMap(([_, record, i18n]) => {
                if (record.FreezePages) {
                    return this.modalService.openModal(i18n[i18nKeys.PSTWebConfirmUnfreezePages], '', ModalResponseCodes.OK, ModalResponseCodes.CANCEL).result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        map(() => record)
                    );
                }
                if (record?.MosControl) {
                    return this.modalService.openModal(i18n[i18nKeys.PSTWebConfirmFreezePages], '', ModalResponseCodes.OK, ModalResponseCodes.CANCEL).result.pipe(
                        filter(res => res === ModalResponseCodes.OK),
                        map(() => record)
                    );
                }
                return of(record);
            }),
            switchMap(rundown => {
                return from(this.rundownService.freezePages(rundown)).pipe(
                    switchMap(res => {
                        const storyRecords: StorytellingStoryRecord[] = res.data.Pages.map(page => {
                            const chunks = page.split('\t');
                            if (chunks.length > 1) {
                                return { Guid: chunks[0], PageNum: chunks[1] };
                            }
                            return { Guid: chunks[0] };
                        });
                        return [
                            RundownRecordActions.updateRundowns({ records: [{ FreezePages: res.data.PagesFrozen, Guid: rundown.Guid }] }),
                            StoriesRecordActions.updateStories({ records: storyRecords }),
                        ];
                    })
                );
            })
        );
    });

    updateRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.onAir, LiveApiActions.mosControlActive, LiveApiActions.mosReadyToAir, LiveApiActions.lockRundown, LiveApiActions.updateCurrentRundown),
            concatLatestFrom(action => this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.guid))),
            exhaustMap(([action, rundown]) => {
                let data = action?.data || {};
                switch (action.type) {
                    case LiveApiActions.onAir.type:
                        data = { ...data, OnAir: !rundown.OnAir };
                        break;
                    case LiveApiActions.mosControlActive.type:
                        data = { ...data, MosControl: !rundown.MosControl };
                        break;
                    case LiveApiActions.mosReadyToAir.type:
                        data = { ...data, MOSAir: !rundown.MOSAir };
                        break;
                    case LiveApiActions.lockRundown.type:
                        data = { ...data, Locked: !rundown.Locked, LockedBy: this.nomWebServicesJson.HelloResults?.UserID, LockTime: new Date().toISOString() };
                        break;
                }
                return from(this.rundownService.updateRundown(rundown, { ...data, type: rundown.Type })).pipe(
                    switchMap(() => from(this.rundownService.getRundown(rundown.Database, rundown.Path.fullPath, rundown.Guid)))
                );
            }),
            switchMap(res => {
                return [RundownRecordActions.updateRundowns({ records: [res.data] }), StoriesRecordActions.updateStories({ records: res.data.children })];
            })
        );
    });

    updateRundownStory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.updateRundownStory),
            concatLatestFrom(action => this.store.select<StorytellingStoryRecord>(selectStoryRecordByGuid(action.guid))),
            map(([action, story]) => {
                let fieldsToUpdate: Array<EnpsField> = [{ key: action.propID, value: action.value[action.propID] }];
                if (action.propID === PSTFieldNamesEnum.Slug) {
                    // TODO Make separator not hardcoded
                    fieldsToUpdate = [{ key: PSTFieldNamesEnum.Title, value: `${action.value[action.propID]}-${story.Segment}` }];
                }

                if (action.propID === PSTFieldNamesEnum.Segment) {
                    fieldsToUpdate = [{ key: PSTFieldNamesEnum.Title, value: `${story.Slug}-${action.value[action.propID]}` }];
                }
                return RecordHttpActions.saveRecordChanges({ fieldsToUpdate, record: story });
            })
        );
    });

    addStoryToRundown$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.addRundownStory),
            concatLatestFrom(action => this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.guid))),
            mergeMap(([action, rundown]) => {
                return from(this.rundownService.addStoryToRundown(rundown, action.scriptDatabase, action.scriptPath, action.scriptGuid, action.insertBeforeGuid, action.propertiesToOverride)).pipe(
                    switchMap(res => {
                        const fullscreenRecordGuid = this.store.selectSignal(selectFullscreenRecordGuid)();
                        const actions = [];
                        actions.push(RundownRecordActions.updateRundowns({ records: [res.data] }));
                        actions.push(StoriesRecordActions.updateStories({ records: res.data.children }));
                        actions.push(
                            StoriesRecordActions.deleteStories({
                                records: [
                                    {
                                        Guid: action.scriptGuid,
                                    },
                                ],
                            })
                        );

                        // Update the listGuids with the new script guid, if it exists
                        const listGuids = this.store.selectSignal(selectListGuids)();
                        const oldScriptIdx = listGuids?.indexOf(action.scriptGuid);
                        if (oldScriptIdx > -1) {
                            const newScriptIdx = res.data.children.findIndex(script => script.OriginalGUID === action.scriptGuid);
                            if (newScriptIdx > -1) {
                                listGuids?.splice(oldScriptIdx, 1, res.data.children[newScriptIdx].Guid);
                                actions.push(ListActions.setListGuids({ listGuids: new Set(listGuids) }));
                            }
                        }

                        // find the story that was just created and update the fullscreen record guid if it is the previous one
                        if (fullscreenRecordGuid === action.scriptGuid) {
                            // find the story in the res.data.children
                            const story = res.data.children.find(script => script.OriginalGUID === fullscreenRecordGuid);
                            if (story) {
                                actions.push(FullscreenActions.openStory({ recordGuid: story.Guid, recordType: ObjectTypes.SCRIPT }));
                            }
                        }

                        return actions;
                    }),
                    catchError(error => of(LiveApiActions.updateRundownStoryError({ error })))
                );
            })
        );
    });

    insertRundownRow$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.insertRundownRow),
            concatLatestFrom(action => this.store.select<StorytellingRundownRecord>(selectRundownRecordByGuid(action.collectionGuid))),
            mergeMap(([action, rundown]) => {
                return from(this.rundownService.insertRowInRundown(rundown, action.database, action.collectionPath, action.collectionGuid, action.insertBeforeGuid, action.fieldsToUpdate)).pipe(
                    switchMap(res => {
                        return [RundownRecordActions.updateRundowns({ records: [res.data] }), StoriesRecordActions.updateStories({ records: res.data.children })];
                    }),
                    catchError(error => of(LiveApiActions.updateRundownStoryError({ error })))
                );
            })
        );
    });

    loadRundownViewStates$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LiveApiActions.loadRundownViewStates),
            switchMap(() => {
                return from(this.viewStateService.getViewStateRecords('live')).pipe(
                    switchMap(({ data }) => {
                        return of(LiveApiActions.loadRundownViewStatesSuccess({ data }), ApplicationActions.setCurrentRundownViewState({ guid: data?.views?.[0]?.guid }));
                    }),
                    catchError(error => of(LiveApiActions.loadRundownsError({ error })))
                );
            })
        );
    });
}
